import React, { useEffect, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Drawer,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Helmet } from 'react-helmet-async';
import CookieConsent from 'react-cookie-consent';
import { useAuth } from '../context/AuthContext';
import BankruptcyStartDialog from '../components/landing/BankruptcyStartDialog';
import SiteFooter, { FooterCta } from '../components/landing/SiteFooter';
import publicTariffsData from '../generated/publicTariffs.json';

const HEADER_HEIGHT = 88;
const PAGE_TITLE = 'Анализ банковских выписок и поиск подозрительных платежей | ФинРадар';
const META_DESCRIPTION = 'Анализируйте банковские выписки, транзакции и контрагентов онлайн. Находите подозрительные платежи и риски за несколько минут. Бесплатный доступ без карты.';
const OG_TITLE = 'Анализ банковских выписок и поиск подозрительных платежей | ФинРадар';
const OG_DESCRIPTION = 'Анализируйте банковские выписки, транзакции и контрагентов онлайн. Находите подозрительные платежи и риски за несколько минут. Бесплатный доступ без карты.';
const CANONICAL_URL = 'https://finradar.brpadvice.ru/';
const META_KEYWORDS = 'анализ банковских выписок, анализ транзакций, поиск подозрительных платежей, анализ контрагентов, проверка контрагентов по выпискам, анализ движения денежных средств, подозрительные операции, поиск рисков по банковским выпискам';

const navItems = [
    { label: 'Возможности', target: '#structure' },
    { label: 'Задачи', target: '#tasks' },
    { label: 'Тарифы', target: '#pricing' },
    { label: 'FAQ', target: '#faq' },
];

const heroBullets = [
    '7 дней бесплатно',
    'Без банковской карты',
    'Можно начать сразу после регистрации',
];

const sourceCards = [
    {
        title: 'Банковские выписки',
        description: 'Соберите движение денег по счетам в одном проекте без ручной склейки файлов и бесконечной работы в Excel.',
        icon: AccountBalanceWalletOutlinedIcon,
    },
    {
        title: 'Книги покупок',
        description: 'Дополняйте картину по расчётам, поставщикам и операциям, чтобы видеть не только деньги, но и хозяйственный контур.',
        icon: ReceiptLongOutlinedIcon,
    },
    {
        title: 'Книги продаж',
        description: 'Смотрите исходящий поток сделок и сопоставляйте его с банковским движением.',
        icon: SellOutlinedIcon,
    },
    {
        title: 'Контрагенты и сделки',
        description: 'Работайте не с отдельными файлами, а с общей структурой по проекту, где проще увидеть связи, обороты и значимые эпизоды.',
        icon: HubOutlinedIcon,
    },
];

const outcomeCards = [
    {
        title: 'Контрагенты и расчёты',
        description: 'Быстрее видно, кто участвовал в движении денег, на какие суммы и в каких объёмах.',
        icon: CorporateFareOutlinedIcon,
    },
    {
        title: 'Сделки и значимые эпизоды',
        description: 'Проще выделить операции и связи, которые требуют отдельной проверки.',
        icon: SchemaOutlinedIcon,
    },
    {
        title: 'Операции по типам',
        description: 'Удобнее отделять нужные группы платежей и быстрее находить то, что важно именно для вашей задачи.',
        icon: CategoryOutlinedIcon,
    },
    {
        title: 'Зоны внимания',
        description: 'Нетипичные движения, спорные расчёты и другие эпизоды, которые стоит посмотреть в первую очередь.',
        icon: WarningAmberRoundedIcon,
    },
    {
        title: 'Подготовленные данные для практической работы',
        description: 'Используйте собранный массив в спорах, взыскании, проверке заёмщиков, внутренней проверке и других прикладных задачах.',
        icon: DownloadOutlinedIcon,
    },
];

const taskCards = [
    {
        title: 'Работа по делу о банкротстве',
        description: 'Быстро соберите данные по должнику и переходите к контрагентам, расчётам, сделкам и операциям, требующим внимания.',
        icon: GavelOutlinedIcon,
    },
    {
        title: 'Оспаривание сделок',
        description: 'Быстрее находите платежи, связи и эпизоды, которые стоит проверить отдельно.',
        icon: ManageSearchOutlinedIcon,
    },
    {
        title: 'Субсидиарная ответственность',
        description: 'Собирайте фактуру по движениям денег, контрагентам и расчётам без долгой ручной подготовки.',
        icon: BalanceOutlinedIcon,
    },
    {
        title: 'Взыскание дебиторской задолженности',
        description: 'Получайте более понятную картину по расчётам, участникам операций и денежным потокам.',
        icon: PolicyOutlinedIcon,
    },
    {
        title: 'Проверка заёмщиков и контрагентов',
        description: 'Используйте данные по оборотам, расчётам и операциям для скоринга и оценки рисков.',
        icon: CorporateFareOutlinedIcon,
    },
    {
        title: 'Внутренняя проверка и регулярная работа',
        description: 'ФинРадар подходит не только для одного дела, но и для постоянной работы с выписками и финансовыми документами.',
        icon: TimelineOutlinedIcon,
    },
];

const comparisonColumns = [
    {
        title: 'Без ФинРадара',
        items: [
            'Выписки, покупки и продажи - все в разных файлах и форматах',
            'Много времени уходит на ручную склейку и сведение данных',
            'Сложно быстро увидеть, кто основные контрагенты и как распределяются расчёты',
            'Значимые сделки и спорные операции приходится искать вручную',
            'Юрист, помощник или аналитик тратит часы на механическую подготовку',
            'Высокий риск упустить важный эпизод в большом массиве документов, не оспорить сделку и не взыскать дебиторскую задолженность',
        ],
        accent: false,
    },
    {
        title: 'С ФинРадаром',
        items: [
            'Данные собираются в единую понятную картину по проекту',
            'Меньше ручной подготовки и повторяющейся работы',
            'Быстрее видно контрагентов, расчёты, сделки и движение денег',
            'Проще выделить операции и связи, требующие внимания',
            'Можно быстрее перейти к взысканию, оспариванию, субсидиарной ответственности, скорингу и другой практической работе',
            'Меньше времени уходит на подготовку, больше - на решение задачи по существу',
        ],
        accent: true,
    },
];

const steps = [
    {
        title: 'Создайте проект',
        description: 'Откройте новый проект под конкретное дело, компанию или задачу.',
        icon: FolderOutlinedIcon,
    },
    {
        title: 'Загрузите документы',
        description: 'Добавьте банковские выписки и при необходимости другие данные для сопоставления и анализа.',
        icon: UploadFileOutlinedIcon,
    },
    {
        title: 'Получите единую структуру',
        description: 'ФинРадар собирает документы в понятную основу для дальнейшей работы по делу.',
        icon: AutoAwesomeOutlinedIcon,
    },
    {
        title: 'Просмотрите контрагентов, расчёты и операции',
        description: 'Переходите от общего массива к нужным компаниям, сделкам и эпизодам.',
        icon: InsightsOutlinedIcon,
    },
    {
        title: 'Используйте результат в работе',
        description: 'Применяйте подготовленные данные в аналитике, спорах, взыскании и других задачах.',
        icon: TaskAltRoundedIcon,
    },
];

const pricingFamiliesAll = [
    {
        key: 'stream',
        label: 'Потоковая работа',
        title: 'Для постоянного потока дел',
        description: 'Для команд, у которых каждый месяц появляются новые процедуры и новые документы.',
        badge: 'Лимит обновляется каждый месяц',
        selectorPrompt: 'Выбирайте тариф под ваш поток',
        accent: false,
        tabs: [
            {
                key: 'light',
                label: 'Лёгкий',
                price: '3 880 ₽ / месяц',
                priceCaption: 'скидки при оплате за квартал и год',
                headline: '5 000 новых транзакций в месяц',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок: 1, 3 или 12 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: 'business',
                label: 'Бизнес',
                price: '9 610 ₽ / месяц',
                priceCaption: 'скидки при оплате за квартал и год',
                headline: '20 000 новых транзакций в месяц',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок: 1, 3 или 12 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
        ],
    },
    {
        key: 'portfolio',
        label: 'Портфель проектов',
        title: 'Для работы по портфелю проектов',
        description: 'Для команд, у которых одновременно много проектов, но нагрузка по ним идёт неравномерно.',
        badge: 'Единый объём транзакций на 6 месяцев',
        selectorPrompt: 'Выбирайте нужное количество операций',
        accent: true,
        tabs: [
            {
                key: '10000',
                label: '10 000',
                price: '12 490 ₽',
                priceSubline: 'всего за 6 месяцев',
                headline: '10 000 новых транзакций на 6 месяцев',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок доступа: 6 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: '50000',
                label: '50 000',
                price: '28 560 ₽',
                priceSubline: 'всего за 6 месяцев',
                headline: '50 000 новых транзакций на 6 месяцев',
                badge: 'Оптимальный выбор',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок доступа: 6 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: '150000',
                label: '150 000',
                price: '61 400 ₽',
                priceSubline: 'всего за 6 месяцев',
                headline: '150 000 новых транзакций на 6 месяцев',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок доступа: 6 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
        ],
    },
    {
        key: 'case',
        label: 'Разовые дела',
        title: 'Тарифы «По делу»',
        description: 'Для разовых проектов, отдельных должников и конкретных процедур с понятным бюджетом на входе.',
        badge: 'Для одного проекта / одного должника',
        selectorPrompt: 'Выбирайте тариф на основе оборотов по счетам',
        accent: false,
        tabs: [
            {
                key: '300m',
                label: 'до 300 млн',
                price: 'от 4 900 ₽ / проект',
                priceCaption: 'всего за 3 месяца; скидки при оплате на 6 месяцев и год',
                headline: '1 проект по 1 должнику',
                features: [
                    'До 30 000 операций',
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel',
                    'Срок: 3, 6 или 12 месяцев',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: '1b',
                label: 'до 1 млрд',
                price: 'от 5 900 ₽ / проект',
                priceCaption: 'всего за 3 месяца; скидки при оплате на 6 месяцев и год',
                headline: '1 проект по 1 должнику',
                features: [
                    'До 40 000 операций',
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel',
                    'Срок: 3, 6 или 12 месяцев',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: '5b',
                label: 'до 5 млрд',
                price: 'от 7 900 ₽ / проект',
                priceCaption: 'всего за 3 месяца; скидки при оплате на 6 месяцев и год',
                headline: '1 проект по 1 должнику',
                features: [
                    'До 50 000 операций',
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel',
                    'Срок: 3, 6 или 12 месяцев',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
        ],
    },
];

const pricingFamiliesDetailed = [
    {
        key: 'stream',
        label: 'Потоковая работа',
        title: 'Для постоянного потока дел',
        description: 'Для команд, у которых каждый месяц появляются новые процедуры и новые документы.',
        badge: 'Лимит обновляется каждый месяц',
        selectorPrompt: 'Выбирайте тариф под ваш поток',
        accent: false,
        tabs: [
            {
                key: 'light',
                label: 'Лёгкий',
                price: 'от 2 717 ₽ / месяц',
                headline: '5 000 новых транзакций в месяц',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок: 1, 3 или 12 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: 'business',
                label: 'Бизнес',
                price: 'от 6 728 ₽ / месяц',
                headline: '20 000 новых транзакций в месяц',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок: 1, 3 или 12 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
        ],
    },
    {
        key: 'portfolio',
        label: 'Портфель проектов',
        title: 'Для работы по портфелю проектов',
        description: 'Для команд, у которых одновременно много проектов, но нагрузка по ним идёт неравномерно.',
        badge: 'Единый объём транзакций на 6 месяцев',
        selectorPrompt: 'Выбирайте нужное количество операций',
        accent: true,
        tabs: [
            {
                key: '10000',
                label: '10 000',
                price: '12 490 ₽ / 6 месяцев',
                headline: '10 000 новых транзакций на 6 месяцев',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок доступа: 6 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: '50000',
                label: '50 000',
                price: '28 560 ₽ / 6 месяцев',
                headline: '50 000 новых транзакций на 6 месяцев',
                badge: 'Оптимальный выбор',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок доступа: 6 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: '150000',
                label: '150 000',
                price: '61 400 ₽ / 6 месяцев',
                headline: '150 000 новых транзакций на 6 месяцев',
                features: [
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel, Word, TXT, Текстовый PDF',
                    'Срок доступа: 6 месяцев',
                    'Без ограничений по числу проектов анализа',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Отсканированные документы, фотографии и любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
        ],
    },
    {
        key: 'case',
        label: 'Разовые дела',
        title: 'Тарифы «По делу»',
        description: 'Для разовых проектов, отдельных должников и конкретных процедур с понятным бюджетом на входе.',
        badge: 'Для одного проекта / одного должника',
        selectorPrompt: 'Выбирайте тариф на основе оборотов по счетам',
        accent: false,
        tabs: [
            {
                key: '300m',
                label: 'до 300 млн',
                price: 'от 4 900 ₽ / проект',
                headline: '1 проект по 1 должнику',
                features: [
                    'До 30 000 операций',
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel',
                    'Срок: 3, 6 или 12 месяцев',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: '1b',
                label: 'до 1 млрд',
                price: 'от 5 900 ₽ / проект',
                headline: '1 проект по 1 должнику',
                features: [
                    'До 40 000 операций',
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel',
                    'Срок: 3, 6 или 12 месяцев',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
            {
                key: '5b',
                label: 'до 5 млрд',
                price: 'от 7 900 ₽ / проект',
                headline: '1 проект по 1 должнику',
                features: [
                    'До 50 000 операций',
                    'Проверки по ЕГРЮЛ и другим источникам',
                    'В тариф входит: Excel',
                    'Срок: 3, 6 или 12 месяцев',
                    'Без ограничений по количеству счетов',
                ],
                extraNote: 'Любые другие форматы подключаются дополнительно.',
                cta: 'Начать бесплатно',
                more: 'Подробнее о тарифе',
            },
        ],
    },
];

const pricingSectionModes = [
    { key: 'all', label: 'Все тарифы' },
    { key: 'stream', label: 'Для постоянного потока дел' },
    { key: 'portfolio', label: 'Для работы по портфелю проектов' },
    { key: 'case', label: 'Тарифы “По делу”' },
];

const trustStats = [
    { value: '10+ трлн ₽', label: 'проанализированного оборота' },
    { value: '2 000+', label: 'процедур в системе' },
    { value: '7 дней', label: 'бесплатного доступа для старта' },
];

const comparisonTariffSourceMap = {
    light: {
        representative: { group: 'MONTHLY', durationMonths: 1, tariffId: 1 },
        prices: [
            { group: 'MONTHLY', durationMonths: 1, tariffId: 1, label: 'месяц' },
            { group: 'MONTHLY', durationMonths: 3, tariffId: 2, label: 'квартал' },
            { group: 'MONTHLY', durationMonths: 12, tariffId: 4, label: 'год' },
        ],
    },
    business: {
        representative: { group: 'MONTHLY', durationMonths: 1, tariffId: 7 },
        prices: [
            { group: 'MONTHLY', durationMonths: 1, tariffId: 7, label: 'месяц' },
            { group: 'MONTHLY', durationMonths: 3, tariffId: 8, label: 'квартал' },
            { group: 'MONTHLY', durationMonths: 12, tariffId: 10, label: 'год' },
        ],
    },
    p10000: {
        representative: { group: 'TRANSACTIONAL', durationMonths: 6, tariffId: 13 },
        prices: [
            { group: 'TRANSACTIONAL', durationMonths: 6, tariffId: 13, label: '6 месяцев' },
        ],
    },
    p50000: {
        representative: { group: 'TRANSACTIONAL', durationMonths: 6, tariffId: 17 },
        prices: [
            { group: 'TRANSACTIONAL', durationMonths: 6, tariffId: 17, label: '6 месяцев' },
        ],
    },
    p150000: {
        representative: { group: 'TRANSACTIONAL', durationMonths: 6, tariffId: 20 },
        prices: [
            { group: 'TRANSACTIONAL', durationMonths: 6, tariffId: 20, label: '6 месяцев' },
        ],
    },
    c300: {
        representative: { group: 'PER_PROJECT', durationMonths: 3, tariffId: 50 },
        prices: [
            { group: 'PER_PROJECT', durationMonths: 3, tariffId: 50, label: '3 месяца' },
            { group: 'PER_PROJECT', durationMonths: 6, tariffId: 51, label: '6 месяцев' },
            { group: 'PER_PROJECT', durationMonths: 12, tariffId: 52, label: '12 месяцев' },
        ],
    },
    c1000: {
        representative: { group: 'PER_PROJECT', durationMonths: 3, tariffId: 53 },
        prices: [
            { group: 'PER_PROJECT', durationMonths: 3, tariffId: 53, label: '3 месяца' },
            { group: 'PER_PROJECT', durationMonths: 6, tariffId: 54, label: '6 месяцев' },
            { group: 'PER_PROJECT', durationMonths: 12, tariffId: 55, label: '12 месяцев' },
        ],
    },
    c5000: {
        representative: { group: 'PER_PROJECT', durationMonths: 3, tariffId: 56 },
        prices: [
            { group: 'PER_PROJECT', durationMonths: 3, tariffId: 56, label: '3 месяца' },
            { group: 'PER_PROJECT', durationMonths: 6, tariffId: 57, label: '6 месяцев' },
            { group: 'PER_PROJECT', durationMonths: 12, tariffId: 58, label: '12 месяцев' },
        ],
    },
};

const tariffAtomAliases = {
    newTransactions: ['Новые транзакции', 'Максимальное количество транзакций, добавляемых в проект'],
    storage: ['Хранилище транзакций'],
    currentChecks: ['ЕГРЮЛ/ЕГРИП - актуальные сведения'],
    historicalChecks: ['ЕГРЮЛ/ЕГРИП - исторические сведения'],
    autoSearch: ['Affiliated_autosearch'],
};

const findPublicTariffItem = ({ group, durationMonths, tariffId }) => {
    const groupItems = publicTariffsData?.tariffs?.[group];
    if (!Array.isArray(groupItems)) return null;

    const bucket = groupItems.find((item) => item.durationMonths === durationMonths);
    if (!bucket) return null;

    return bucket.items.find((item) => item.tariffId === tariffId) || null;
};

const getRepresentativeTariff = (columnKey) => {
    const source = comparisonTariffSourceMap[columnKey]?.representative;
    return source ? findPublicTariffItem(source) : null;
};

const getTariffAtomLimit = (columnKey, atomKey) => {
    const tariff = getRepresentativeTariff(columnKey);
    const aliases = tariffAtomAliases[atomKey] || [];
    const atom = tariff?.atoms?.find((item) => aliases.includes(item.atomType));
    return atom?.usageLimit ?? null;
};

const formatCount = (value) => {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat('ru-RU').format(value);
};

const formatRub = (value) => {
    if (value === null || value === undefined) return '—';
    return `${new Intl.NumberFormat('ru-RU').format(value)} ₽`;
};

const getNewTransactionsComparisonValue = (columnKey) => {
    const tariff = getRepresentativeTariff(columnKey);
    const atom = tariff?.atoms?.find((item) => tariffAtomAliases.newTransactions.includes(item.atomType));
    if (!atom) return '—';

    const formattedLimit = formatCount(atom.usageLimit);
    if (comparisonTariffSourceMap[columnKey]?.representative.group === 'MONTHLY') {
        return `${formattedLimit} / месяц`;
    }
    if (comparisonTariffSourceMap[columnKey]?.representative.group === 'TRANSACTIONAL') {
        return `${formattedLimit} / 6 месяцев`;
    }
    return `${formattedLimit} / проект`;
};

const getPriceSeries = (columnKey) => {
    const series = comparisonTariffSourceMap[columnKey]?.prices || [];
    return series
        .map((item) => {
            const tariff = findPublicTariffItem(item);
            if (!tariff?.pricing?.price) return null;
            return `${formatRub(tariff.pricing.price)} / ${item.label}`;
        })
        .filter(Boolean);
};

const getTariffPriceValue = (columnKey, durationMonths) => {
    const source = comparisonTariffSourceMap[columnKey]?.prices?.find((item) => item.durationMonths === durationMonths);
    const tariff = source ? findPublicTariffItem(source) : null;
    return tariff?.pricing?.price ?? null;
};

const getDurationLabel = (durationMonths) => {
    if (durationMonths === 1) return 'месяц';
    if (durationMonths === 3) return '3 месяца';
    if (durationMonths === 6) return '6 месяцев';
    if (durationMonths === 12) return '12 месяцев';
    return `${durationMonths} месяцев`;
};

const getStreamPaymentLabel = (durationMonths) => {
    if (durationMonths === 12) return 'при оплате за год';
    if (durationMonths === 3) return 'при оплате за 3 месяца';
    return 'при оплате за месяц';
};

const getMonthlyBillingNote = (fullPrice, durationMonths) => {
    if (!fullPrice || !durationMonths || durationMonths <= 1) return '';
    return `Расчетная цена месяца при единовременной оплате ${formatRub(fullPrice)} за ${getDurationLabel(durationMonths)}`;
};

const getStreamPlanByDuration = (planKey, durationMonths) => {
    const basePlan = pricingFamiliesDetailed[0].tabs.find((item) => item.key === planKey);
    const price = getTariffPriceValue(planKey, durationMonths);
    const monthPrice = getTariffPriceValue(planKey, 1);
    const monthlyEquivalent = price ? Math.ceil(price / durationMonths) : null;
    const savings = durationMonths > 1 && monthPrice ? monthPrice * durationMonths - price : 0;

    return {
        ...basePlan,
        price: monthlyEquivalent ? `${formatRub(monthlyEquivalent)} / месяц` : basePlan.price,
        durationMonths,
        priceSubline: getMonthlyBillingNote(price, durationMonths) || getStreamPaymentLabel(durationMonths),
        savingsNote: durationMonths > 1 && savings > 0 ? `Экономия ${formatRub(savings)} относительно помесячной оплаты` : '',
    };
};

const portfolioColumnKeyMap = {
    '10000': 'p10000',
    '50000': 'p50000',
    '150000': 'p150000',
};

const caseColumnKeyMap = {
    '300m': 'c300',
    '1b': 'c1000',
    '5b': 'c5000',
};

const getCasePlanByDuration = (planKey, durationMonths) => {
    const basePlan = pricingFamiliesDetailed[2].tabs.find((item) => item.key === planKey);
    const columnKey = caseColumnKeyMap[planKey];
    const price = getTariffPriceValue(columnKey, durationMonths);
    const basePrice = getTariffPriceValue(columnKey, 3);
    const multiplier = durationMonths / 3;
    const savings = durationMonths > 3 && basePrice ? basePrice * multiplier - price : 0;

    return {
        ...basePlan,
        price: price ? `от ${formatRub(price)} / проект` : basePlan.price,
        durationMonths,
        priceSubline: `всего за ${getDurationLabel(durationMonths)}`,
        savingsNote: durationMonths > 3 && savings > 0 ? `Экономия ${formatRub(savings)} относительно оплаты по 3 месяца` : '',
    };
};

const getPortfolioPlan = (plan) => {
    const columnKey = portfolioColumnKeyMap[plan.key];
    const price = getTariffPriceValue(columnKey, 6);

    return {
        ...plan,
        price: price ? `${formatRub(price)}` : plan.price,
        durationMonths: 6,
        priceSubline: 'всего за 6 месяцев',
        savingsNote: '',
    };
};

const getPricingModeColumns = (mode) => {
    if (mode === 'stream') return pricingComparisonColumns.filter((column) => ['light', 'business'].includes(column.key));
    if (mode === 'portfolio') return pricingComparisonColumns.filter((column) => ['p10000', 'p50000', 'p150000'].includes(column.key));
    if (mode === 'case') return pricingComparisonColumns.filter((column) => ['c300', 'c1000', 'c5000'].includes(column.key));
    return pricingComparisonColumns;
};

const getPricingModeGroups = (mode) => {
    if (mode === 'stream') return pricingComparisonGroups.filter((group) => group.columns.every((column) => ['light', 'business'].includes(column)));
    if (mode === 'portfolio') return pricingComparisonGroups.filter((group) => group.columns.every((column) => ['p10000', 'p50000', 'p150000'].includes(column)));
    if (mode === 'case') return pricingComparisonGroups.filter((group) => group.columns.every((column) => ['c300', 'c1000', 'c5000'].includes(column)));
    return pricingComparisonGroups;
};

const pricingComparisonColumns = [
    { key: 'light', label: 'Лёгкий' },
    { key: 'business', label: 'Бизнес' },
    { key: 'p10000', label: '10 000 операций' },
    { key: 'p50000', label: '50 000 операций' },
    { key: 'p150000', label: '150 000 операций' },
    { key: 'c300', label: 'до 300 млн ₽' },
    { key: 'c1000', label: 'до 1 млрд ₽' },
    { key: 'c5000', label: 'до 5 млрд ₽' },
];

const pricingComparisonGroups = [
    { label: 'Для постоянного потока дел', columns: ['light', 'business'] },
    { label: 'Для работы по портфелю проектов', columns: ['p10000', 'p50000', 'p150000'] },
    { label: 'Тарифы «По делу»', columns: ['c300', 'c1000', 'c5000'] },
];

const streamDurationOptions = [
    { value: 1, label: 'Месяц' },
    { value: 3, label: '3 месяца' },
    { value: 12, label: '12 месяцев' },
];

const caseDurationOptions = [
    { value: 3, label: '3 месяца' },
    { value: 6, label: '6 месяцев' },
    { value: 12, label: '12 месяцев' },
];

const pricingComparisonRows = [
    {
        label: 'Цены',
        values: {
            light: getPriceSeries('light'),
            business: getPriceSeries('business'),
            p10000: getPriceSeries('p10000'),
            p50000: getPriceSeries('p50000'),
            p150000: getPriceSeries('p150000'),
            c300: getPriceSeries('c300'),
            c1000: getPriceSeries('c1000'),
            c5000: getPriceSeries('c5000'),
        },
    },
    {
        label: 'Новые транзакции',
        tooltip: 'Загрузка выписок, книг покупок и продаж учитывается по количеству операций в документе.',
        values: {
            light: getNewTransactionsComparisonValue('light'),
            business: getNewTransactionsComparisonValue('business'),
            p10000: getNewTransactionsComparisonValue('p10000'),
            p50000: getNewTransactionsComparisonValue('p50000'),
            p150000: getNewTransactionsComparisonValue('p150000'),
            c300: getNewTransactionsComparisonValue('c300'),
            c1000: getNewTransactionsComparisonValue('c1000'),
            c5000: getNewTransactionsComparisonValue('c5000'),
        },
    },
    {
        label: 'Хранилище транзакций',
        tooltip: 'Количество операций по всем проектам, учитывая предыдущие загрузки, которое хранится в ФинРадаре. Не нужные проекты можно удалять или архивировать - тогда они не учитываются в этом лимите.',
        values: {
            light: formatCount(getTariffAtomLimit('light', 'storage')),
            business: formatCount(getTariffAtomLimit('business', 'storage')),
            p10000: formatCount(getTariffAtomLimit('p10000', 'storage')),
            p50000: formatCount(getTariffAtomLimit('p50000', 'storage')),
            p150000: formatCount(getTariffAtomLimit('p150000', 'storage')),
            c300: formatCount(getTariffAtomLimit('c300', 'storage')),
            c1000: formatCount(getTariffAtomLimit('c1000', 'storage')),
            c5000: formatCount(getTariffAtomLimit('c5000', 'storage')),
        },
    },
    {
        label: 'Проверки по ЕГРЮЛ и другим источникам (актуальные сведения)',
        values: {
            light: formatCount(getTariffAtomLimit('light', 'currentChecks')),
            business: formatCount(getTariffAtomLimit('business', 'currentChecks')),
            p10000: formatCount(getTariffAtomLimit('p10000', 'currentChecks')),
            p50000: formatCount(getTariffAtomLimit('p50000', 'currentChecks')),
            p150000: formatCount(getTariffAtomLimit('p150000', 'currentChecks')),
            c300: formatCount(getTariffAtomLimit('c300', 'currentChecks')),
            c1000: formatCount(getTariffAtomLimit('c1000', 'currentChecks')),
            c5000: formatCount(getTariffAtomLimit('c5000', 'currentChecks')),
        },
    },
    {
        label: 'Исторические сведения ЕГРЮЛ, ЕГРИП',
        values: {
            light: formatCount(getTariffAtomLimit('light', 'historicalChecks')),
            business: formatCount(getTariffAtomLimit('business', 'historicalChecks')),
            p10000: formatCount(getTariffAtomLimit('p10000', 'historicalChecks')),
            p50000: formatCount(getTariffAtomLimit('p50000', 'historicalChecks')),
            p150000: formatCount(getTariffAtomLimit('p150000', 'historicalChecks')),
            c300: formatCount(getTariffAtomLimit('c300', 'historicalChecks')),
            c1000: formatCount(getTariffAtomLimit('c1000', 'historicalChecks')),
            c5000: formatCount(getTariffAtomLimit('c5000', 'historicalChecks')),
        },
    },
    {
        label: 'Количество экспортов операций в Excel',
        values: {
            light: 'Не ограничено',
            business: 'Не ограничено',
            p10000: 'Не ограничено',
            p50000: 'Не ограничено',
            p150000: 'Не ограничено',
            c300: 'Не ограничено',
            c1000: 'Не ограничено',
            c5000: 'Не ограничено',
        },
    },
    {
        label: 'Лимит экспорта операций в Excel в каждом файле',
        values: {
            light: '1 000',
            business: '1 000',
            p10000: '1 000',
            p50000: '1 000',
            p150000: '1 000',
            c300: '1 000',
            c1000: '1 000',
            c5000: '1 000',
        },
    },
    {
        label: 'Лимит количества контрагентов в экспортируемых в Excel отчетах в каждом файле',
        values: {
            light: '200',
            business: '200',
            p10000: '200',
            p50000: '200',
            p150000: '200',
            c300: '200',
            c1000: '200',
            c5000: '200',
        },
    },
    {
        label: 'Возможность увеличения лимитов',
        values: {
            light: 'Тарифная опция',
            business: 'Тарифная опция',
            p10000: 'Тарифная опция',
            p50000: 'Тарифная опция',
            p150000: 'Тарифная опция',
            c300: 'Тарифная опция',
            c1000: 'Тарифная опция',
            c5000: 'Тарифная опция',
        },
    },
    {
        label: 'Экспорт отчетов по расчетам с контрагентом в Word',
        values: {
            light: 'Не ограничено',
            business: 'Не ограничено',
            p10000: 'Не ограничено',
            p50000: 'Не ограничено',
            p150000: 'Не ограничено',
            c300: 'Не ограничено',
            c1000: 'Не ограничено',
            c5000: 'Не ограничено',
        },
    },
    {
        label: 'Число проектов',
        values: {
            light: 'Не ограничено',
            business: 'Не ограничено',
            p10000: 'Не ограничено',
            p50000: 'Не ограничено',
            p150000: 'Не ограничено',
            c300: '1 проект',
            c1000: '1 проект',
            c5000: '1 проект',
        },
    },
    {
        label: 'Число расчетных счетов',
        values: {
            light: 'Не ограничено',
            business: 'Не ограничено',
            p10000: 'Не ограничено',
            p50000: 'Не ограничено',
            p150000: 'Не ограничено',
            c300: 'Не ограничено',
            c1000: 'Не ограничено',
            c5000: 'Не ограничено',
        },
    },
    {
        label: 'Автоматический поиск двух поколений связей по владельцу счета на основе ЕГРЮЛ',
        values: {
            light: formatCount(getTariffAtomLimit('light', 'autoSearch')),
            business: formatCount(getTariffAtomLimit('business', 'autoSearch')),
            p10000: formatCount(getTariffAtomLimit('p10000', 'autoSearch')),
            p50000: formatCount(getTariffAtomLimit('p50000', 'autoSearch')),
            p150000: formatCount(getTariffAtomLimit('p150000', 'autoSearch')),
            c300: formatCount(getTariffAtomLimit('c300', 'autoSearch')),
            c1000: formatCount(getTariffAtomLimit('c1000', 'autoSearch')),
            c5000: formatCount(getTariffAtomLimit('c5000', 'autoSearch')),
        },
    },
    {
        label: 'Форматы, включенные в тариф',
        values: {
            light: 'Excel, Word, TXT, Текстовый PDF',
            business: 'Excel, Word, TXT, Текстовый PDF',
            p10000: 'Excel, Word, TXT, Текстовый PDF',
            p50000: 'Excel, Word, TXT, Текстовый PDF',
            p150000: 'Excel, Word, TXT, Текстовый PDF',
            c300: 'Excel',
            c1000: 'Excel',
            c5000: 'Excel',
        },
    },
    {
        label: 'Подключение дополнительных текстовых форматов',
        values: {
            light: '870 ₽ за 1 банковский формат / проект',
            business: '870 ₽ за 1 банковский формат / проект',
            p10000: '870 ₽ за 1 банковский формат / проект',
            p50000: '870 ₽ за 1 банковский формат / проект',
            p150000: '870 ₽ за 1 банковский формат / проект',
            c300: '870 ₽ за 1 банковский формат / проект',
            c1000: '870 ₽ за 1 банковский формат / проект',
            c5000: '870 ₽ за 1 банковский формат / проект',
        },
    },
    {
        label: 'Отсканированные документы и фотографии',
        values: {
            light: 'Тарифицируется отдельно после бесплатной оценки возможности и точности извлечения данных',
            business: 'Тарифицируется отдельно после бесплатной оценки возможности и точности извлечения данных',
            p10000: 'Тарифицируется отдельно после бесплатной оценки возможности и точности извлечения данных',
            p50000: 'Тарифицируется отдельно после бесплатной оценки возможности и точности извлечения данных',
            p150000: 'Тарифицируется отдельно после бесплатной оценки возможности и точности извлечения данных',
            c300: 'Тарифицируется отдельно после бесплатной оценки возможности и точности извлечения данных',
            c1000: 'Тарифицируется отдельно после бесплатной оценки возможности и точности извлечения данных',
            c5000: 'Тарифицируется отдельно после бесплатной оценки возможности и точности извлечения данных',
        },
    },
    {
        label: 'Анализ книг покупок и продаж',
        values: {
            light: '2 000 ₽ / проект',
            business: 'Включено',
            p10000: 'Включено',
            p50000: 'Включено',
            p150000: 'Включено',
            c300: '2 000 ₽ / проект',
            c1000: '3 000 ₽ / проект',
            c5000: '4 000 ₽ / проект',
        },
    },
];

const whyImportantItems = [
    'меньше ручной сборки данных;',
    'быстрее видна общая картина по проекту;',
    'проще перейти от документов к рабочему анализу.',
];

const faqItems = [
    {
        question: 'Как анализировать банковские выписки?',
        answer: [
            'Анализ банковских выписок включает сбор и структурирование данных по операциям, счетам и контрагентам. Вручную это требует работы в Excel и занимает часы или дни.',
            'В ФинРадар процесс автоматизирован: вы создаёте проект, загружаете выписки (Excel, PDF, TXT) и система сама собирает операции в единую структуру. После этого вы получаете готовую картину движения средств, контрагентов и взаимосвязей между ними.',
        ],
    },
    {
        question: 'Как найти подозрительные платежи?',
        answer: [
            'Подозрительные платежи обычно выявляются через анализ структуры операций: частые переводы между одними и теми же компаниями, дробление сумм, нетипичные назначения платежа, вывод средств через цепочки контрагентов.',
            'ФинРадар помогает находить такие операции автоматически: система группирует транзакции, показывает связи между участниками и выделяет зоны внимания, где есть риск аффилированности, транзитных операций или вывода средств.',
        ],
    },
    {
        question: 'Можно ли загрузить Excel, PDF, Word, TXT и другие форматы?',
        answer: [
            'Да, ФинРадар поддерживает загрузку банковских выписок в форматах Excel, PDF, Word и TXT, а также многие другие форматы, включая отсканированные документы.',
            'Вы можете загрузить файлы из разных банков и за разные периоды – система приведёт их к единому формату и объединит в один проект для дальнейшего анализа согласно выбранному тарифу.',
        ],
    },
    {
        question: 'Сколько времени занимает анализ?',
        answer: [
            'Базовый анализ начинается сразу после загрузки файлов.',
            'В большинстве случаев первые результаты – структура операций, контрагенты и основные взаимосвязи – доступны в течение нескольких минут. Время зависит от объёма данных, формата и количества загруженных файлов.',
        ],
    },
    {
        question: 'Подходит ли для банкротства?',
        answer: [
            'Да, ФинРадар изначально создавался для задач, связанных с банкротством и финансовыми расследованиями.',
            'Сервис помогает анализировать движение средств, выявлять подозрительные сделки, находить взаимосвязанные компании и готовить основу для дальнейшей правовой оценки – например, для оспаривания операций или анализа финансового поведения должника.',
        ],
    },
    {
        question: 'Что входит в бесплатный доступ?',
        answer: [
            'Бесплатный доступ действует 7 дней и позволяет создать 1 проект, загрузить документы и оценить, как ФинРадар собирает данные в единую рабочую структуру.',
        ],
    },
    {
        question: 'Подходит ли ФинРадар только для арбитражных управляющих?',
        answer: [
            'Нет. ФинРадар особенно полезен для работы по делам о банкротстве, но также подходит для споров, взыскания, внутренней аналитики и задач, где нужно быстро собрать и структурировать финансовые данные.',
        ],
    },
    {
        question: 'Можно ли использовать ФинРадар для работы по конкретному делу?',
        answer: [
            'Да. Для этого у ФинРадара есть отдельная линейка тарифов «По делу» – с понятным входом под конкретный проект, должника или процедуру.',
        ],
    },
    {
        question: 'Чем отличаются тарифы для регулярной работы и тарифы «По делу»?',
        answer: [
            'Тарифы для регулярной работы подходят тем, кто постоянно загружает документы и работает с аналитикой. Тарифы «По делу» подходят тем, кто работает с конкретным проектом и хочет начать с более экономичного варианта, подключая дополнительные форматы по мере необходимости.',
        ],
    },
    {
        question: 'Можно ли анализировать вместе выписки, покупки и продажи?',
        answer: [
            'Да. В этом и одна из ключевых ценностей ФинРадара: сервис помогает собрать выписки, книги покупок и книги продаж в одну рабочую картину по проекту.',
        ],
    },
    {
        question: 'ФинРадар подходит только для больших массивов данных?',
        answer: [
            'Нет. Его можно использовать и для отдельных дел, и для регулярной аналитической нагрузки.',
        ],
    },
];

const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer.join('\n\n'),
        },
    })),
};

const sectionStyles = {
    scrollMarginTop: `${HEADER_HEIGHT + 24}px`,
    py: { xs: 8, md: 11 },
};

function scrollToHash() {
    const hash = window.location.hash;
    if (!hash) return;

    const element = document.querySelector(hash);
    if (!element) return;

    const top = element.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT;
    window.scrollTo({ top, behavior: 'smooth' });
}

function SectionHeading({ title, description, eyebrow, align = 'left' }) {
    return (
        <Stack spacing={2} sx={{ maxWidth: 820, mb: 5, textAlign: align }}>
            {eyebrow ? (
                <Typography variant="overline" sx={{ color: '#5A6472', fontWeight: 700, letterSpacing: '0.12em' }}>
                    {eyebrow}
                </Typography>
            ) : null}
            <Typography
                variant="h2"
                sx={{
                    fontSize: { xs: 30, md: 42 },
                    lineHeight: 1.15,
                    fontWeight: 700,
                    color: '#101828',
                    fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
                }}
            >
                {title}
            </Typography>
            {description ? (
                <Typography sx={{ color: '#475467', lineHeight: 1.8, fontSize: { xs: 16, md: 18 } }}>
                    {description}
                </Typography>
            ) : null}
        </Stack>
    );
}

function FAQAnswer({ answer, sx }) {
    return (
        <Stack spacing={2}>
            {answer.map((paragraph) => (
                <Typography key={paragraph} sx={sx}>
                    {paragraph}
                </Typography>
            ))}
        </Stack>
    );
}

function ValueList({ items }) {
    return (
        <Stack spacing={1.4}>
            {items.map((item) => (
                <Stack key={item} direction="row" spacing={1.4} alignItems="flex-start">
                    <CheckCircleOutlineRoundedIcon sx={{ color: '#155EEF', mt: '2px', fontSize: 20 }} />
                    <Typography sx={{ color: '#344054', lineHeight: 1.7 }}>{item}</Typography>
                </Stack>
            ))}
        </Stack>
    );
}

function StartWorkButton({ onClick, children = 'Попробовать 7 дней бесплатно', ...props }) {
    return <Button onClick={onClick} {...props}>{children}</Button>;
}

function FeatureCard({ title, description, icon: Icon, accent = false }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 3,
                borderRadius: 5,
                backgroundColor: accent ? '#F5F8FF' : '#FFFFFF',
                borderColor: accent ? '#B2CCFF' : '#D0D5DD',
                height: '100%',
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3.5,
                    backgroundColor: '#EEF4FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#155EEF',
                    mb: 2.5,
                }}
            >
                <Icon />
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#101828', lineHeight: 1.35 }}>
                {title}
            </Typography>
            <Typography sx={{ mt: 1.5, color: '#667085', lineHeight: 1.75 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function PriceDisplay({ value }) {
    return <>{value}</>;
}

function ComparisonLabel({ label, tooltip }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <Typography sx={{ fontWeight: 600, color: '#101828', lineHeight: 1.5 }}>
                {label}
            </Typography>
            {tooltip ? (
                <Tooltip title={tooltip} arrow placement="top">
                    <InfoOutlinedIcon sx={{ color: '#98A2B3', fontSize: 18, mt: '2px' }} />
                </Tooltip>
            ) : null}
        </Stack>
    );
}

function ComparisonValue({ value }) {
    if (Array.isArray(value)) {
        return (
            <Stack spacing={0.5}>
                {value.map((item) => (
                    <Typography key={item} sx={{ color: '#344054', lineHeight: 1.55, fontSize: 14 }}>
                        {item}
                    </Typography>
                ))}
            </Stack>
        );
    }

    return (
        <Typography sx={{ color: '#344054', lineHeight: 1.55, fontSize: 14 }}>
            {value}
        </Typography>
    );
}

function PricingFamilyCard({ family, selectedTab, onTabChange, onPrimaryClick, onMoreClick }) {
    const activePlan = family.tabs.find((tab) => tab.key === selectedTab) || family.tabs[0];
    const hasSelector = Boolean(family.selectorPrompt) && family.tabs.length > 1;
    const pricingHeaderMinHeight = {
        xs: 'auto',
        md: '212px',
    };
    const selectorPromptMinHeight = {
        xs: 'auto',
        md: '56px',
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 6,
                backgroundColor: family.accent ? '#F5F8FF' : '#FFFFFF',
                borderColor: family.accent ? '#98B8FF' : '#D0D5DD',
                boxShadow: family.accent ? '0 20px 60px rgba(21, 94, 239, 0.12)' : 'none',
                height: '100%',
            }}
        >
            <CardContent sx={{ p: 3.5, height: '100%' }}>
                <Stack spacing={0} sx={{ height: '100%' }}>
                    <Stack
                        spacing={1.5}
                        sx={{
                            minHeight: pricingHeaderMinHeight,
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Chip
                            label={family.badge}
                            sx={{
                                alignSelf: 'flex-start',
                                backgroundColor: family.accent ? '#155EEF' : '#101828',
                                color: '#FFFFFF',
                                fontWeight: 700,
                            }}
                        />
                        <Box>
                            <Typography sx={{ fontSize: 26, fontWeight: 700, color: '#101828', lineHeight: 1.3 }}>
                                {family.title}
                            </Typography>
                            <Typography sx={{ mt: 1.5, color: '#667085', lineHeight: 1.75 }}>
                                {family.description}
                            </Typography>
                        </Box>
                    </Stack>
                    {hasSelector ? (
                        <Stack spacing={1.25} sx={{ mt: 0 }}>
                            <Box
                                sx={{
                                    minHeight: selectorPromptMinHeight,
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <Typography sx={{ color: '#475467', fontWeight: 600, lineHeight: 1.5 }}>
                                    {family.selectorPrompt}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {family.tabs.map((tab) => {
                                    const isSelected = tab.key === selectedTab;

                                    return (
                                        <Button
                                            key={tab.key}
                                            variant="text"
                                            onClick={() => onTabChange(tab.key)}
                                            sx={{
                                                minHeight: 0,
                                                py: 1,
                                                px: 1.6,
                                                minWidth: 0,
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                border: '1px solid',
                                                borderColor: isSelected ? '#B2CCFF' : '#D0D5DD',
                                                color: isSelected ? '#155EEF' : '#475467',
                                                backgroundColor: isSelected ? '#EEF4FF' : '#FFFFFF',
                                                '&:hover': {
                                                    backgroundColor: isSelected ? '#E0ECFF' : '#F9FAFB',
                                                    borderColor: isSelected ? '#98B8FF' : '#D0D5DD',
                                                },
                                            }}
                                        >
                                            {tab.label}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Stack>
                    ) : null}

                    <Paper variant="outlined" sx={{ mt: hasSelector ? 3 : 2, p: 3, borderRadius: 5, backgroundColor: '#FFFFFF' }}>
                        <Stack spacing={2.25}>
                            <Box>
                                <Typography sx={{ fontSize: { xs: 22, md: 27 }, fontWeight: 800, color: '#101828', lineHeight: 1.1 }}>
                                    <PriceDisplay value={activePlan.price} />
                                </Typography>
                                {activePlan.priceSubline || activePlan.priceCaption ? (
                                    <Typography sx={{ mt: 0.75, color: '#344054', fontSize: { xs: 15, md: 16 }, fontWeight: 700, lineHeight: 1.5 }}>
                                        {activePlan.priceSubline || activePlan.priceCaption}
                                    </Typography>
                                ) : null}
                                {activePlan.savingsNote ? (
                                    <Typography sx={{ mt: 0.25, color: '#067647', fontSize: { xs: 15, md: 16 }, fontWeight: 700, lineHeight: 1.5 }}>
                                        {activePlan.savingsNote}
                                    </Typography>
                                ) : null}
                                <Typography sx={{ mt: 1, color: '#155EEF', fontWeight: 700, lineHeight: 1.6 }}>
                                    {activePlan.headline}
                                </Typography>
                            </Box>

                            <ValueList items={activePlan.features} />

                            {activePlan.extras ? (
                                <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 4, backgroundColor: '#F9FAFB' }}>
                                    <Typography sx={{ color: '#101828', fontWeight: 700, mb: 1.25 }}>
                                        Дополнительно
                                    </Typography>
                                    <ValueList items={activePlan.extras} />
                                </Paper>
                            ) : null}

                            {activePlan.extraNote ? (
                                <Typography sx={{ color: '#667085', fontSize: 14, lineHeight: 1.7 }}>
                                    {activePlan.extraNote}
                                </Typography>
                            ) : null}
                        </Stack>
                    </Paper>

                    <Stack spacing={1.25} sx={{ mt: 'auto', pt: 3 }}>
                        <Button
                            variant={family.accent ? 'contained' : 'outlined'}
                            onClick={onPrimaryClick}
                            fullWidth
                            sx={{
                                borderRadius: 999,
                                py: 1.4,
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: 16,
                            }}
                        >
                            {activePlan.cta}
                        </Button>
                        <Button
                            variant="text"
                            onClick={onMoreClick}
                            sx={{
                                textTransform: 'none',
                                alignSelf: 'flex-start',
                                px: 0,
                                color: '#155EEF',
                                fontWeight: 700,
                            }}
                        >
                            {activePlan.more}
                        </Button>
                        <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap">
                            <Chip
                                label="Поддерживаются: юрлица, ИП, физлица"
                                sx={{ mt: 1, mr: 1, borderRadius: 999, backgroundColor: '#F2F4F7', color: '#344054' }}
                            />
                            <Chip
                                label="Поддержка: email, мессенджеры"
                                sx={{ mt: 1, mr: 1, borderRadius: 999, backgroundColor: '#F2F4F7', color: '#344054' }}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function Main2LandingPage() {
    const { isAuthenticated } = useAuth();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [startDialogOpen, setStartDialogOpen] = useState(false);
    const [startDialogInitialTab, setStartDialogInitialTab] = useState('register');
    const [pricingViewMode, setPricingViewMode] = useState('all');
    const [streamPlan, setStreamPlan] = useState('light');
    const [streamDuration, setStreamDuration] = useState(12);
    const [portfolioPlan, setPortfolioPlan] = useState('50000');
    const [casePlan, setCasePlan] = useState('1b');
    const [caseDuration, setCaseDuration] = useState(3);
    const [pricingComparisonOpen, setPricingComparisonOpen] = useState(false);
    const pricingComparisonRef = useRef(null);

    useEffect(() => {
        scrollToHash();
        window.addEventListener('hashchange', scrollToHash);

        return () => window.removeEventListener('hashchange', scrollToHash);
    }, []);

    const handleNavClick = (event, target) => {
        event?.preventDefault();
        setDrawerOpen(false);

        const element = document.querySelector(target);
        if (!element) return;

        const top = element.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT;
        window.scrollTo({ top, behavior: 'smooth' });

        if (window.location.hash !== target) {
            window.history.pushState(null, '', target);
        }
    };

    const handleOpenStartDialog = () => {
        setDrawerOpen(false);
        setStartDialogInitialTab('register');
        setStartDialogOpen(true);
    };

    const handleOpenLogin = () => {
        setDrawerOpen(false);

        if (isAuthenticated) {
            window.location.href = '/app/';
            return;
        }

        setStartDialogInitialTab('login');
        setStartDialogOpen(true);
    };

    const handleOpenPricingComparison = () => {
        setPricingComparisonOpen(true);

        window.requestAnimationFrame(() => {
            pricingComparisonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    const expandedStreamCards = ['light', 'business'].map((planKey) => ({
        ...pricingFamiliesDetailed[0],
        title: pricingFamiliesDetailed[0].tabs.find((item) => item.key === planKey)?.label,
        description: pricingFamiliesDetailed[0].description,
        selectorPrompt: '',
        tabs: [getStreamPlanByDuration(planKey, streamDuration)],
        accent: planKey === 'business',
    }));

    const expandedPortfolioCards = pricingFamiliesDetailed[1].tabs.map((plan) => ({
        ...pricingFamiliesDetailed[1],
        title: `${plan.label} операций`,
        description: pricingFamiliesDetailed[1].description,
        selectorPrompt: '',
        tabs: [getPortfolioPlan(plan)],
        accent: plan.key === '50000',
    }));

    const expandedCaseCards = ['300m', '1b', '5b'].map((planKey) => ({
        ...pricingFamiliesDetailed[2],
        title: pricingFamiliesDetailed[2].tabs.find((item) => item.key === planKey)?.label,
        description: pricingFamiliesDetailed[2].description,
        selectorPrompt: '',
        tabs: [getCasePlanByDuration(planKey, caseDuration)],
        accent: planKey === '1b',
    }));

    const allModeFamilies = [
        {
            ...pricingFamiliesAll[0],
            accent: false,
        },
        {
            ...pricingFamiliesAll[1],
            accent: false,
        },
        {
            ...pricingFamiliesAll[2],
            accent: true,
        },
    ];

    const visiblePricingColumns = getPricingModeColumns(pricingViewMode);
    const visiblePricingGroups = getPricingModeGroups(pricingViewMode);

    return (
        <Box sx={{ backgroundColor: '#F5F7FA' }}>
            <Helmet>
                <title>{PAGE_TITLE}</title>
                <meta name="description" content={META_DESCRIPTION} />
                <meta name="keywords" content={META_KEYWORDS} />
                <meta property="og:title" content={OG_TITLE} />
                <meta property="og:description" content={OG_DESCRIPTION} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={CANONICAL_URL} />
                <link rel="canonical" href={CANONICAL_URL} />
                <script type="application/ld+json">
                    {JSON.stringify(faqStructuredData)}
                </script>
            </Helmet>

            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.92)',
                    color: '#101828',
                    borderBottom: '1px solid #EAECF0',
                    backdropFilter: 'blur(16px)',
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ minHeight: `${HEADER_HEIGHT}px`, gap: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 3,
                                    background: 'linear-gradient(135deg, #0F172A 0%, #1D4ED8 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#FFFFFF',
                                    fontWeight: 800,
                                }}
                            >
                                FR
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
                                    ФинРадар
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#667085', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                    Реальная картина бизнеса
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                            {navItems.map((item) => (
                                <Button
                                    key={item.target}
                                    component="a"
                                    href={item.target}
                                    onClick={(event) => handleNavClick(event, item.target)}
                                    sx={{ color: '#344054', px: 2 }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                            <Button
                                component="a"
                                href="/blog/"
                                sx={{ color: '#344054', px: 2 }}
                            >
                                Блог
                            </Button>
                        </Stack>

                        <Stack direction="row" spacing={1.25} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                            <StartWorkButton
                                onClick={handleOpenStartDialog}
                                variant="contained"
                                sx={{
                                    borderRadius: 999,
                                    px: 3,
                                    py: 1.3,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    backgroundColor: '#155EEF',
                                }}
                            />
                            <Button
                                onClick={handleOpenLogin}
                                variant="text"
                                sx={{
                                    borderRadius: 999,
                                    px: 2,
                                    py: 1.2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    color: '#344054',
                                }}
                            >
                                Вход
                            </Button>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
                            <StartWorkButton
                                onClick={handleOpenStartDialog}
                                variant="contained"
                                sx={{
                                    borderRadius: 999,
                                    px: 2.25,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    backgroundColor: '#155EEF',
                                }}
                            >
                                Попробовать
                            </StartWorkButton>
                            <Button
                                onClick={handleOpenLogin}
                                variant="outlined"
                                sx={{
                                    borderRadius: 999,
                                    px: 2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderColor: '#D0D5DD',
                                    color: '#344054',
                                    minWidth: 0,
                                }}
                            >
                                Вход
                            </Button>
                            <IconButton onClick={() => setDrawerOpen(true)} sx={{ border: '1px solid #D0D5DD', borderRadius: 3 }}>
                                <MenuIcon />
                            </IconButton>
                        </Stack>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 300, p: 3 }}>
                    <Stack spacing={2}>
                        {navItems.map((item) => (
                            <ListItemButton
                                key={item.target}
                                component="a"
                                href={item.target}
                                onClick={(event) => handleNavClick(event, item.target)}
                                sx={{ borderRadius: 3 }}
                            >
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        ))}
                        <ListItemButton
                            component="a"
                            href="/blog/"
                            sx={{ borderRadius: 3 }}
                        >
                            <ListItemText primary="Блог" />
                        </ListItemButton>
                        <Button
                            onClick={handleOpenLogin}
                            variant="outlined"
                            fullWidth
                            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
                        >
                            Вход
                        </Button>
                        <StartWorkButton
                            onClick={handleOpenStartDialog}
                            variant="contained"
                            fullWidth
                            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
                        />
                    </Stack>
                </Box>
            </Drawer>

            <Box
                component="section"
                sx={{
                    pt: { xs: 7, md: 10 },
                    pb: { xs: 8, md: 12 },
                    background:
                        'radial-gradient(circle at top right, rgba(21, 94, 239, 0.08), transparent 28%), linear-gradient(180deg, #F8FAFC 0%, #F5F7FA 100%)',
                }}
            >
                <Container maxWidth="xl">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} lg={6}>
                            <Stack spacing={4}>
                                <Box>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            fontSize: { xs: 38, md: 58 },
                                            lineHeight: 1.05,
                                            fontWeight: 800,
                                            color: '#101828',
                                            letterSpacing: '-0.03em',
                                            fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
                                            maxWidth: 760,
                                        }}
                                    >
                                        ФинРадар – онлайн анализ выписок и сделок в задачах банкротства, кредитования и проверки бизнеса
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 3,
                                            fontSize: { xs: 17, md: 19 },
                                            lineHeight: 1.8,
                                            color: '#475467',
                                            maxWidth: 720,
                                        }}
                                    >
                                        С тотальной проверкой сделок быстрее находите значимые операции, контролируемых контрагентов, подозрительные сделки и цепочки расчётов по выпискам, книгам покупок и продаж должника.
                                    </Typography>
                                </Box>

                                <Grid container spacing={1.5}>
                                    {heroBullets.map((item) => (
                                        <Grid item xs={12} sm={4} key={item}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 4,
                                                    height: '100%',
                                                    backgroundColor: 'rgba(255,255,255,0.78)',
                                                }}
                                            >
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            backgroundColor: '#155EEF',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography sx={{ color: '#344054', fontWeight: 600 }}>{item}</Typography>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <StartWorkButton
                                        onClick={handleOpenStartDialog}
                                        variant="contained"
                                        endIcon={<ArrowForwardRoundedIcon />}
                                        sx={{
                                            borderRadius: 999,
                                            px: 3.5,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontSize: 16,
                                            fontWeight: 700,
                                            backgroundColor: '#155EEF',
                                        }}
                                    />
                                    <Button
                                        component="a"
                                        href="#structure"
                                        onClick={(event) => handleNavClick(event, '#structure')}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 999,
                                            px: 3.5,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontSize: 16,
                                            fontWeight: 700,
                                            borderColor: '#BFC7D4',
                                            color: '#344054',
                                        }}
                                    >
                                        Посмотреть возможности
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} lg={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 6,
                                    border: '1px solid #D0D5DD',
                                    overflow: 'hidden',
                                    background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
                                    boxShadow: '0 30px 80px rgba(15, 23, 42, 0.12)',
                                }}
                            >
                                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #EAECF0', backgroundColor: '#FFFFFF' }}>
                                    <Stack spacing={0.75}>
                                        <Typography sx={{ fontWeight: 700, color: '#0F172A' }}>
                                            Реальная картина бизнеса по документам, а не вручную собранные таблицы
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#667085' }}>
                                            ФинРадар помогает быстрее увидеть контрагентов, расчёты, сделки и операции, требующие внимания.
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Box sx={{ height: { xs: 320, md: 420 }, overflow: 'hidden', backgroundColor: '#FFFFFF' }}>
                                    <Box
                                        component="img"
                                        src="/assets/images/пример рисков по выписке.webp"
                                        alt="Интерфейс ФинРадар"
                                        sx={{
                                            display: 'block',
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            objectPosition: 'center top',
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box id="structure" component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        title="Что вы получите после анализа банковских выписок"
                        description="ФинРадар помогает собрать данные из разных источников в одну структуру, с которой удобно работать дальше: анализировать движение денег, смотреть контрагентов, сопоставлять расчёты и выделять сделки."
                    />
                    <Grid container spacing={2.5}>
                        {sourceCards.map((card) => (
                            <Grid item xs={12} sm={6} key={card.title}>
                                <FeatureCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                    <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 5, backgroundColor: '#FFFFFF' }}>
                        <Typography sx={{ color: '#344054', fontWeight: 600, textAlign: 'center' }}>
                            Один проект – одна рабочая картина вместо набора разрозненных документов.
                        </Typography>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        title="На выходе – данные, с которыми можно работать по делу"
                        description="ФинРадар помогает не просто загрузить документы, а быстро собрать из них понятную основу для взыскания, оспаривания сделок, субсидиарной ответственности, скоринга и проверки контрагентов."
                    />
                    <Grid container spacing={2.5}>
                        {outcomeCards.map((card, index) => (
                            <Grid item xs={12} sm={6} lg={index === outcomeCards.length - 1 ? 12 : 6} key={card.title}>
                                <FeatureCard {...card} accent={index === 0} />
                            </Grid>
                        ))}
                    </Grid>
                    <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 5, backgroundColor: '#F9FAFB' }}>
                        <Typography sx={{ color: '#344054', fontWeight: 600, textAlign: 'center' }}>
                            ФинРадар сокращает время от загрузки документов до практической работы по делу.
                        </Typography>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        title="Что меняется с ФинРадаром"
                        description="ФинРадар убирает лишнюю ручную работу и быстро выводит на полезный результат по делу или проекту."
                    />
                    <Grid container spacing={3}>
                        {comparisonColumns.map((column) => (
                            <Grid item xs={12} md={6} key={column.title}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 3.5,
                                        borderRadius: 6,
                                        backgroundColor: column.accent ? '#F5F8FF' : '#FFFFFF',
                                        borderColor: column.accent ? '#98B8FF' : '#D0D5DD',
                                        height: '100%',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#101828', mb: 2.5 }}>
                                        {column.title}
                                    </Typography>
                                    <ValueList items={column.items} />
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                    <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 5, backgroundColor: '#FFFFFF' }}>
                        <Typography sx={{ color: '#344054', fontWeight: 600, textAlign: 'center' }}>
                            ФинРадар – это сокращение ручной работы и более быстрый выход на значимые факты по делу.
                        </Typography>
                    </Paper>
                </Container>
            </Box>

            <Box id="tasks" component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        title="Кому необходимы анализ банковских выписок, поиск подозрительных платежей и контрагентов"
                        description="Когда нужно не просто разобрать документы, а быстрее выйти на практический результат по делу или проекту."
                    />
                    <Grid container spacing={2.5}>
                        {taskCards.map((card) => (
                            <Grid item xs={12} sm={6} lg={4} key={card.title}>
                                <FeatureCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                    <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 5, backgroundColor: '#FFFFFF' }}>
                        <Typography sx={{ color: '#344054', fontWeight: 600, textAlign: 'center' }}>
                            Один и тот же инструмент помогает и по конкретному делу, и в регулярной работе с финансовыми данными.
                        </Typography>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        title="Как провести анализ банковских выписок, покупок и продаж за 5 минут"
                        description="От загрузки документов до рабочей картины – за несколько шагов."
                    />
                    <Grid container spacing={2}>
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            return (
                                <Grid item xs={12} md={6} lg key={step.title}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 3,
                                            borderRadius: 5,
                                            backgroundColor: '#FFFFFF',
                                            height: '100%',
                                            minHeight: 260,
                                        }}
                                    >
                                        <Stack spacing={2.25}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography sx={{ color: '#155EEF', fontWeight: 800 }}>0{index + 1}</Typography>
                                                <Box
                                                    sx={{
                                                        width: 46,
                                                        height: 46,
                                                        borderRadius: 3,
                                                        backgroundColor: '#EEF4FF',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#155EEF',
                                                    }}
                                                >
                                                    <Icon />
                                                </Box>
                                            </Stack>
                                            <Typography sx={{ color: '#101828', fontWeight: 700, fontSize: 19, lineHeight: 1.45 }}>
                                                {step.title}
                                            </Typography>
                                            <Typography sx={{ color: '#667085', lineHeight: 1.75 }}>
                                                {step.description}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                    <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 5, backgroundColor: '#F9FAFB' }}>
                        <Typography sx={{ color: '#344054', fontWeight: 600, textAlign: 'center' }}>
                            Не нужно начинать с ручной сборки данных – можно сразу переходить к сути.
                        </Typography>
                    </Paper>
                </Container>
            </Box>

            <Box id="pricing" component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        title="Тарифы под ваш формат работы"
                        description="Один и тот же ФинРадар можно подключить для постоянного потока дел, для работы по портфелю проектов и для отдельных дел. Если задачи разные, тарифы можно сочетать."
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
                        {pricingSectionModes.map((mode) => {
                            const isSelected = pricingViewMode === mode.key;
                            return (
                                <Chip
                                    key={mode.key}
                                    label={mode.label}
                                    onClick={() => setPricingViewMode(mode.key)}
                                    clickable
                                    sx={{
                                        mb: 1,
                                        borderRadius: 999,
                                        backgroundColor: isSelected ? '#EEF4FF' : '#FFFFFF',
                                        border: '1px solid',
                                        borderColor: isSelected ? '#B2CCFF' : '#D0D5DD',
                                        color: isSelected ? '#155EEF' : '#344054',
                                        fontWeight: 700,
                                    }}
                                />
                            );
                        })}
                    </Stack>

                    {pricingViewMode === 'stream' ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 3 }}>
                            <Typography sx={{ color: '#475467', fontWeight: 600, mb: 1 }}>
                                При единовременной оплате:
                            </Typography>
                            {streamDurationOptions.map((option) => {
                                const isSelected = streamDuration === option.value;
                                return (
                                    <Chip
                                        key={option.value}
                                        label={option.label}
                                        onClick={() => setStreamDuration(option.value)}
                                        clickable
                                        sx={{
                                            mb: 1,
                                            borderRadius: 999,
                                            backgroundColor: isSelected ? '#EEF4FF' : '#FFFFFF',
                                            border: '1px solid',
                                            borderColor: isSelected ? '#B2CCFF' : '#D0D5DD',
                                            color: isSelected ? '#155EEF' : '#344054',
                                            fontWeight: 700,
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    ) : null}

                    {pricingViewMode === 'case' ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 3 }}>
                            <Typography sx={{ color: '#475467', fontWeight: 600, mb: 1 }}>
                                При сроке доступа к проекту:
                            </Typography>
                            {caseDurationOptions.map((option) => {
                                const isSelected = caseDuration === option.value;
                                return (
                                    <Chip
                                        key={option.value}
                                        label={option.label}
                                        onClick={() => setCaseDuration(option.value)}
                                        clickable
                                        sx={{
                                            mb: 1,
                                            borderRadius: 999,
                                            backgroundColor: isSelected ? '#EEF4FF' : '#FFFFFF',
                                            border: '1px solid',
                                            borderColor: isSelected ? '#B2CCFF' : '#D0D5DD',
                                            color: isSelected ? '#155EEF' : '#344054',
                                            fontWeight: 700,
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    ) : null}

                    <Grid container spacing={3}>
                        {pricingViewMode === 'all' ? (
                            <>
                                <Grid item xs={12} md={6} lg={4}>
                                    <PricingFamilyCard
                                        family={allModeFamilies[0]}
                                        selectedTab={streamPlan}
                                        onTabChange={setStreamPlan}
                                        onPrimaryClick={handleOpenStartDialog}
                                        onMoreClick={handleOpenPricingComparison}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6} lg={4}>
                                    <PricingFamilyCard
                                        family={allModeFamilies[1]}
                                        selectedTab={portfolioPlan}
                                        onTabChange={setPortfolioPlan}
                                        onPrimaryClick={handleOpenStartDialog}
                                        onMoreClick={handleOpenPricingComparison}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12} lg={4}>
                                    <PricingFamilyCard
                                        family={allModeFamilies[2]}
                                        selectedTab={casePlan}
                                        onTabChange={setCasePlan}
                                        onPrimaryClick={handleOpenStartDialog}
                                        onMoreClick={handleOpenPricingComparison}
                                    />
                                </Grid>
                            </>
                        ) : null}

                        {pricingViewMode === 'stream'
                            ? expandedStreamCards.map((family) => (
                                <Grid item xs={12} md={6} key={family.title}>
                                    <PricingFamilyCard
                                        family={family}
                                        selectedTab={family.tabs[0].key}
                                        onTabChange={() => { }}
                                        onPrimaryClick={handleOpenStartDialog}
                                        onMoreClick={handleOpenPricingComparison}
                                    />
                                </Grid>
                            ))
                            : null}

                        {pricingViewMode === 'portfolio'
                            ? expandedPortfolioCards.map((family) => (
                                <Grid item xs={12} md={6} lg={4} key={family.title}>
                                    <PricingFamilyCard
                                        family={family}
                                        selectedTab={family.tabs[0].key}
                                        onTabChange={() => { }}
                                        onPrimaryClick={handleOpenStartDialog}
                                        onMoreClick={handleOpenPricingComparison}
                                    />
                                </Grid>
                            ))
                            : null}

                        {pricingViewMode === 'case'
                            ? expandedCaseCards.map((family) => (
                                <Grid item xs={12} md={6} lg={4} key={family.title}>
                                    <PricingFamilyCard
                                        family={family}
                                        selectedTab={family.tabs[0].key}
                                        onTabChange={() => { }}
                                        onPrimaryClick={handleOpenStartDialog}
                                        onMoreClick={handleOpenPricingComparison}
                                    />
                                </Grid>
                            ))
                            : null}
                    </Grid>

                    <Accordion
                        ref={pricingComparisonRef}
                        expanded={pricingComparisonOpen}
                        onChange={(_, expanded) => setPricingComparisonOpen(expanded)}
                        disableGutters
                        elevation={0}
                        sx={{
                            mt: 3,
                            borderRadius: '24px !important',
                            border: '1px solid #D0D5DD',
                            backgroundColor: '#FFFFFF',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack spacing={0.5}>
                                <Typography sx={{ color: '#101828', fontWeight: 700 }}>
                                    Подробное сравнение тарифов
                                </Typography>
                                <Typography sx={{ color: '#667085', lineHeight: 1.6, fontSize: 14 }}>
                                    Ниже – все цены, сроки, лимиты и форматы по каждому варианту.
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <TableContainer
                                sx={{
                                    border: '1px solid #EAECF0',
                                    borderRadius: 4,
                                    overflowX: 'auto',
                                    maxHeight: 820,
                                }}
                            >
                                <Table stickyHeader sx={{ minWidth: 1440 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    minWidth: 280,
                                                    fontWeight: 700,
                                                    color: '#101828',
                                                    backgroundColor: '#F9FAFB',
                                                    top: 0,
                                                    zIndex: 3,
                                                }}
                                            >
                                                Параметр
                                            </TableCell>
                                            {visiblePricingGroups.map((group) => (
                                                <TableCell
                                                    key={group.label}
                                                    align="center"
                                                    colSpan={group.columns.length}
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: '#101828',
                                                        backgroundColor: '#F9FAFB',
                                                        top: 0,
                                                        zIndex: 3,
                                                    }}
                                                >
                                                    {group.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    minWidth: 280,
                                                    fontWeight: 700,
                                                    color: '#101828',
                                                    backgroundColor: '#F9FAFB',
                                                    top: 57,
                                                    zIndex: 3,
                                                }}
                                            >
                                                Тариф
                                            </TableCell>
                                            {visiblePricingColumns.map((column) => (
                                                <TableCell
                                                    key={column.key}
                                                    sx={{
                                                        minWidth: 160,
                                                        verticalAlign: 'top',
                                                        fontWeight: 700,
                                                        color: '#101828',
                                                        backgroundColor: '#F9FAFB',
                                                        top: 57,
                                                        zIndex: 3,
                                                    }}
                                                >
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pricingComparisonRows.map((row) => (
                                            <TableRow key={row.label} hover>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <ComparisonLabel label={row.label} tooltip={row.tooltip} />
                                                </TableCell>
                                                {visiblePricingColumns.map((column) => (
                                                    <TableCell key={`${row.label}-${column.key}`} sx={{ verticalAlign: 'top' }}>
                                                        <ComparisonValue value={row.values[column.key]} />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>

                    <Paper variant="outlined" sx={{ mt: 3, p: 3.5, borderRadius: 5, backgroundColor: '#FFFFFF' }}>
                        <Stack spacing={2}>
                            <Typography sx={{ color: '#475467', lineHeight: 1.8, textAlign: 'center' }}>
                                Не уверены, какой вариант подходит? Начните с бесплатного доступа и посмотрите, как ФинРадар собирает данные в единую рабочую структуру.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                                {['7 дней бесплатно', '1 проект', 'Без банковской карты'].map((item) => (
                                    <Chip
                                        key={item}
                                        label={item}
                                        sx={{
                                            height: 40,
                                            borderRadius: 999,
                                            backgroundColor: '#EEF4FF',
                                            color: '#1849A9',
                                            fontWeight: 700,
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={{ ...sectionStyles, pt: { xs: 2, md: 4 } }}>
                <Container maxWidth="lg">
                    <Paper
                        variant="outlined"
                        sx={{
                            p: { xs: 4, md: 5 },
                            borderRadius: 6,
                            backgroundColor: '#EAF2FF',
                            border: '1px solid #C7DAFF',
                        }}
                    >
                        <Stack spacing={3}>
                            <SectionHeading
                                title="Попробуйте ФинРадар бесплатно 7 дней"
                                description="Создайте проект, загрузите документы и посмотрите на своих данных, сколько ручной работы можно убрать уже на старте."
                            />
                            <Grid container spacing={1.5}>
                                {['1 проект', '7 дней бесплатно', 'Без банковской карты', 'Можно начать сразу после регистрации'].map((item) => (
                                    <Grid item xs={12} sm={6} key={item}>
                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                            <CheckCircleOutlineRoundedIcon sx={{ color: '#155EEF', fontSize: 20 }} />
                                            <Typography sx={{ color: '#344054', fontWeight: 600 }}>{item}</Typography>
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                            <Stack spacing={2}>
                                <Typography sx={{ color: '#344054', lineHeight: 1.85, fontSize: 17 }}>
                                    Бесплатный доступ нужен, чтобы вы могли не читать о сервисе, а проверить его на своей задаче.
                                    Загрузите документы и посмотрите, как ФинРадар помогает быстрее собрать картину по контрагентам, расчётам, сделкам и операциям.
                                </Typography>
                                <Typography sx={{ color: '#344054', lineHeight: 1.85, fontSize: 17 }}>
                                    Если вы работаете по конкретному делу – начните с проекта под это дело.
                                    Если у вас регулярный поток документов – используйте бесплатный доступ, чтобы понять, насколько сервис сокращает ручную подготовку и упрощает дальнейшую работу.
                                </Typography>
                            </Stack>
                            <Stack spacing={1.25} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                                <StartWorkButton
                                    onClick={handleOpenStartDialog}
                                    variant="contained"
                                    sx={{
                                        borderRadius: 999,
                                        px: 3.5,
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: 16,
                                        backgroundColor: '#155EEF',
                                    }}
                                />
                                <Typography sx={{ color: '#667085', fontSize: 14 }}>
                                    Без банковской карты. Можно проверить сервис на своих данных до оплаты.
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        title="ФинРадар уже помогает работать с большими массивами финансовых данных"
                        description="Сервис используют и для отдельных проектов, и для регулярной аналитической работы."
                    />
                    <Grid container spacing={3} alignItems="stretch">
                        <Grid item xs={12} lg={7}>
                            <Grid container spacing={2}>
                                {trustStats.map((item) => (
                                    <Grid item xs={12} md={4} key={item.value}>
                                        <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 5, backgroundColor: '#FFFFFF', height: '100%' }}>
                                            <Typography sx={{ fontSize: { xs: 34, md: 42 }, fontWeight: 800, color: '#101828' }}>
                                                {item.value}
                                            </Typography>
                                            <Typography sx={{ mt: 1, color: '#667085', lineHeight: 1.7 }}>
                                                {item.label}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 5, backgroundColor: '#FFFFFF', height: '100%' }}>
                                <Stack spacing={3}>
                                    <Typography sx={{ color: '#475467', lineHeight: 1.85 }}>
                                        ФинРадар помогает быстрее собрать рабочую базу по документам и перейти к анализу по существу:
                                        контрагенты, расчёты, сделки, подозрительные операции и зоны внимания.
                                    </Typography>
                                    <Typography sx={{ color: '#475467', lineHeight: 1.85 }}>
                                        Сервис подходит, когда нужно работать как с отдельным проектом, так и с постоянным потоком документов.
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4, backgroundColor: '#F9FAFB' }}>
                                        <Typography sx={{ color: '#101828', fontWeight: 700, mb: 1.5 }}>
                                            Почему это важно
                                        </Typography>
                                        <ValueList items={whyImportantItems} />
                                    </Paper>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box id="faq" component="section" sx={sectionStyles}>
                <Container maxWidth="lg">
                    <SectionHeading title="Часто задаваемые вопросы об анализе банковских выписок" />
                    <List disablePadding sx={{ display: 'grid', gap: 1.5 }}>
                        {faqItems.map((item) => (
                            <Accordion
                                key={item.question}
                                defaultExpanded
                                disableGutters
                                elevation={0}
                                sx={{
                                    borderRadius: '20px !important',
                                    border: '1px solid #D0D5DD',
                                    backgroundColor: '#FFFFFF',
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ color: '#101828', fontWeight: 700 }}>
                                        {item.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <FAQAnswer
                                        answer={item.answer}
                                        sx={{ color: '#475467', lineHeight: 1.8 }}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </List>
                </Container>
            </Box>

            <FooterCta onRegisterClick={handleOpenStartDialog} />

            <BankruptcyStartDialog
                open={startDialogOpen}
                onClose={() => setStartDialogOpen(false)}
                isAuthenticated={isAuthenticated}
                initialAuthTab={startDialogInitialTab}
            />

            <CookieConsent
                location="bottom"
                cookieName="finradar_main2_cookie_consent"
                buttonText="Продолжить"
                expires={365}
                disableStyles
                containerClasses="finradar-cookie-consent"
                contentClasses="finradar-cookie-consent-content"
                buttonClasses="finradar-cookie-consent-button"
                style={{
                    position: 'fixed',
                    left: 16,
                    right: 16,
                    bottom: 16,
                    zIndex: 1400,
                    margin: '0 auto',
                    maxWidth: 1120,
                    borderRadius: 24,
                    background: 'rgba(16, 24, 40, 0.96)',
                    color: '#FFFFFF',
                    boxShadow: '0 24px 60px rgba(16, 24, 40, 0.28)',
                    padding: '18px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                }}
                buttonStyle={{
                    margin: 0,
                    border: 'none',
                    borderRadius: 999,
                    background: '#FFFFFF',
                    color: '#101828',
                    padding: '12px 18px',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        display: 'inline',
                        color: '#FFFFFF',
                        lineHeight: 1.7,
                        fontSize: { xs: 14, md: 15 },
                    }}
                >
                    Мы используем cookie и Яндекс Метрику для анализа работы сайта. В Личном кабинете сбор данных Метрикой не ведется.
                    Продолжайте работу, если вы согласны с условиями. Подробнее{' '}
                    <Box
                        component="a"
                        href="https://brpadvice.ru/politika-konfidencialnosti/"
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                            color: '#B2DDFF',
                            fontWeight: 700,
                            textDecorationColor: 'rgba(178, 221, 255, 0.72)',
                        }}
                    >
                        в Политике конфиденциальности
                    </Box>
                    .
                </Typography>
            </CookieConsent>

            <SiteFooter onRegisterClick={handleOpenStartDialog} />
        </Box>
    );
}

export default Main2LandingPage;
